import { EllipsisOutlined, PlusOutlined, SearchOutlined, VideoCameraOutlined } from '@ant-design/icons'
import { Button, Dropdown, Empty, Input, Pagination, Skeleton, Tabs, message } from 'antd'
import type { MenuProps } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  createPublishItem,
  listPublishItems,
  videoGenTaskPosterUrl,
  type PublishItemSummary,
  type PublishTabKey,
} from '../api'
import VideoPickerModal from '../components/VideoPickerModal/VideoPickerModal'
import type { PickableVideo } from '../api'
import { formatDateTime } from '../../i18n/dateFormat'
import { useUiLocale } from '../../i18n/LocaleProvider'
import './content-publishing.css'
import '../ui/tenant-items.css'

const PAGE_SIZE = 10

const STATUS_BADGE_CLASS: Record<string, string> = {
  draft: 'tc-badge-neutral',
  ready_for_publisher: 'tc-badge-info',
  scheduled: 'tc-badge-info',
  queued: 'tc-badge-info',
  waiting_for_local_agent: 'tc-badge-warning',
  publishing: 'tc-badge-warning',
  awaiting_human_confirmation: 'tc-badge-warning',
  verifying_result: 'tc-badge-info',
  published: 'tc-badge-success',
  failed: 'tc-badge-error',
  failed_retryable: 'tc-badge-error',
  failed_final: 'tc-badge-error',
  cancelled: 'tc-badge-neutral',
}

function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds == null) return null
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function publishStatusLabel(status: string, t: (key: string, options?: { defaultValue?: string }) => string): string {
  const localized = t(`publishing:status.${status}`, { defaultValue: '' })
  if (localized) return localized
  return t(`status:${status}`, { defaultValue: status })
}

function publishTabLabel(key: PublishTabKey, t: (key: string, options?: { defaultValue?: string }) => string): string {
  return t(`publishing:tabs.${key}`, { defaultValue: key })
}

export default function ContentPublishingListPage() {
  const { t } = useTranslation(['publishing', 'actions', 'status'])
  const { workspaceId = '' } = useParams()
  const navigate = useNavigate()

  const [tab, setTab] = useState<PublishTabKey>('all')
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<PublishItemSummary[]>([])
  const [tabs, setTabs] = useState<{ key: PublishTabKey; label: string; count: number }[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQ(q.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [q])

  const load = useCallback(async () => {
    if (!workspaceId) return
    setLoading(true)
    try {
      const { data } = await listPublishItems(workspaceId, {
        tab,
        q: debouncedQ || undefined,
        page,
        page_size: PAGE_SIZE,
      })
      setItems(data.items)
      setTabs(data.tabs)
      setTotal(data.total)
    } catch (e) {
      message.error((e as Error).message || t('publishing:toast.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [workspaceId, tab, debouncedQ, page, t])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    setPage(1)
  }, [tab])

  const onPickVideo = async (video: PickableVideo) => {
    setPickerOpen(false)
    setCreating(true)
    try {
      const { data } = await createPublishItem(workspaceId, video.id)
      message.success(t('publishing:toast.draftCreated'))
      navigate(`/workspace/${workspaceId}/content-publishing/${data.id}`)
    } catch (e) {
      message.error((e as Error).message || t('publishing:toast.createFailed'))
    } finally {
      setCreating(false)
    }
  }

  const noResultsAtAll = !loading && total === 0 && tab === 'all' && !debouncedQ

  const tabItems = useMemo(
    () =>
      (tabs.length > 0
        ? tabs
        : [{ key: 'all' as PublishTabKey, label: publishTabLabel('all', t), count: 0 }]
      ).map((tabItem) => ({
        key: tabItem.key,
        label: `${publishTabLabel(tabItem.key, t)} ${tabItem.count}`,
      })),
    [tabs, t],
  )

  return (
    <div className="tc-items-page">
      <div className="tc-page-header">
        <div>
          <h2 className="tc-page-title">{t('publishing:page.title')}</h2>
          <p className="tc-page-desc">{t('publishing:page.subtitle')}</p>
        </div>
        <div className="tc-page-actions">
          <Button type="primary" icon={<PlusOutlined />} loading={creating} onClick={() => setPickerOpen(true)}>
            {t('publishing:page.newPublish')}
          </Button>
        </div>
      </div>

      <div className="tc-data-card">
        <div className="tc-toolbar" style={{ justifyContent: 'space-between' }}>
          <Tabs activeKey={tab} onChange={(k) => setTab(k as PublishTabKey)} items={tabItems} />
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t('publishing:searchPlaceholder')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 240 }}
          />
        </div>

        {loading && items.length === 0 ? (
          <div style={{ padding: 16 }}>
            <Skeleton active paragraph={{ rows: 3 }} />
          </div>
        ) : noResultsAtAll ? (
          <div className="cp-empty">
            <div className="cp-empty-title">{t('publishing:empty.title')}</div>
            <div className="cp-empty-desc">{t('publishing:empty.description')}</div>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setPickerOpen(true)}>
              {t('publishing:empty.selectVideo')}
            </Button>
          </div>
        ) : items.length === 0 ? (
          <Empty description={t('publishing:empty.noMatch')} style={{ padding: '48px 0' }} />
        ) : (
          <div className="cp-list">
            {items.map((item) => (
              <PublishItemCard
                key={item.id}
                item={item}
                workspaceId={workspaceId}
                onOpen={() => navigate(`/workspace/${workspaceId}/content-publishing/${item.id}`)}
              />
            ))}
          </div>
        )}

        {total > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px' }}>
            <Pagination current={page} pageSize={PAGE_SIZE} total={total} size="small" onChange={setPage} showSizeChanger={false} />
          </div>
        )}
      </div>

      <VideoPickerModal
        open={pickerOpen}
        workspaceId={workspaceId}
        title={t('publishing:picker.title')}
        onCancel={() => setPickerOpen(false)}
        onConfirm={onPickVideo}
      />
    </div>
  )
}

function PublishItemCard({
  item,
  workspaceId,
  onOpen,
}: {
  item: PublishItemSummary
  workspaceId: string
  onOpen: () => void
}) {
  const { t } = useTranslation(['publishing', 'actions', 'status'])
  const { locale } = useUiLocale()
  const [thumbError, setThumbError] = useState(false)
  const duration = formatDuration(item.duration_seconds)

  const menuItems: MenuProps['items'] =
    item.status === 'published'
      ? [{ key: 'view', label: t('publishing:actions.viewResult') }]
      : item.status === 'failed'
        ? [{ key: 'view', label: t('publishing:actions.viewFailure') }]
        : [{ key: 'edit', label: t('publishing:actions.editPublish') }]

  const primaryActionLabel =
    item.status === 'draft'
      ? t('publishing:actions.continueEdit')
      : item.status === 'scheduled'
        ? t('publishing:actions.editSchedule')
        : item.status === 'published'
          ? t('publishing:actions.viewResult')
          : item.status === 'failed'
            ? t('publishing:actions.viewFailure')
            : t('publishing:actions.view')

  return (
    <div className="cp-list-card">
      <div className="cp-list-card-thumb" onClick={onOpen} role="button" tabIndex={0}>
        {thumbError ? (
          <div className="cp-list-card-thumb-fallback">
            <VideoCameraOutlined />
          </div>
        ) : (
          <img src={videoGenTaskPosterUrl(workspaceId, item.source_asset_id)} alt="" onError={() => setThumbError(true)} />
        )}
        <div className="cp-list-card-thumb-play">▶</div>
        {duration && <span className="cp-list-card-duration">{duration}</span>}
      </div>
      <div className="cp-list-card-body">
        <div className="cp-list-card-title-row">
          <span className="cp-list-card-title" onClick={onOpen} title={item.title}>
            {item.title}
          </span>
          <span className={`tc-badge ${STATUS_BADGE_CLASS[item.status] || 'tc-badge-neutral'}`}>
            {publishStatusLabel(item.status, t)}
          </span>
        </div>
        <div className="cp-list-card-meta">
          <span>{t('publishing:card.video')}</span>
          {item.platform_count > 0 ? (
            <span className="cp-list-card-platforms">
              {item.platforms.map((p) => (
                <span key={p} className="cp-platform-pill">
                  {p}
                </span>
              ))}
            </span>
          ) : (
            <span>{t('publishing:card.noPlatform')}</span>
          )}
        </div>
        <div className="cp-list-card-times">
          <span>{t('publishing:card.createdAt', { time: item.created_at ? formatDateTime(item.created_at, locale) : '—' })}</span>
          {item.scheduled_at && <span>{t('publishing:card.scheduledAt', { time: formatDateTime(item.scheduled_at, locale) })}</span>}
        </div>
      </div>
      <div className="cp-list-card-actions">
        <Button size="small" onClick={onOpen}>
          {primaryActionLabel}
        </Button>
        <Dropdown menu={{ items: menuItems, onClick: onOpen }} trigger={['click']}>
          <Button size="small" type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      </div>
    </div>
  )
}
